import React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  SimpleGrid,
  VStack,
} from '@chakra-ui/react';
import { FaPlus, FaTrash } from 'react-icons/fa';

interface QuoteConfigFormProps {
  data: Record<string, any>;
  onChange: (data: Record<string, any>) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export default function QuoteConfigForm({
  data,
  onChange,
  onSubmit,
  isSubmitting,
}: QuoteConfigFormProps) {
  const set = (key: string, value: any) => onChange({ ...data, [key]: value });

  const stateConfig: Record<string, { fica_tax: number; futa_tax: number; suta_tax: number }> =
    data.quote_state_configuration ?? {};

  const updateStateTax = (
    state: string,
    key: 'fica_tax' | 'futa_tax' | 'suta_tax',
    value: number,
  ) => {
    onChange({
      ...data,
      quote_state_configuration: {
        ...stateConfig,
        [state]: { ...stateConfig[state], [key]: value },
      },
    });
  };

  const addState = () => {
    const state = prompt('Enter 2-letter state code (e.g. FL, NJ):')?.toUpperCase();
    if (!state || state.length !== 2) return;
    onChange({
      ...data,
      quote_state_configuration: {
        ...stateConfig,
        [state]: { fica_tax: 7.65, futa_tax: 0.6, suta_tax: 2.7 },
      },
    });
  };

  const removeState = (state: string) => {
    const { [state]: _, ...rest } = stateConfig;
    onChange({ ...data, quote_state_configuration: rest });
  };

  return (
    <VStack align="stretch" spacing={3}>
      <SimpleGrid columns={2} spacing={3}>
        <NumField label="Deductible" value={data.deductible} onChange={(v) => set('deductible', v)} />
        <NumField label="Initial Cost" value={data.initial_cost} onChange={(v) => set('initial_cost', v)} />
        <NumField label="Admin Rate" value={data.admin_rate} onChange={(v) => set('admin_rate', v)} />
        <NumField label="WC Minimums" value={data.wc_minimums} onChange={(v) => set('wc_minimums', v)} />
        <NumField label="Risk Supplemental" value={data.risk_supplemental} onChange={(v) => set('risk_supplemental', v)} />
        <NumField label="Matching Taxes" value={data.matching_taxes} onChange={(v) => set('matching_taxes', v)} />
        <NumField label="Collateral Cost" value={data.collateral_cost} onChange={(v) => set('collateral_cost', v)} />
        <NumField label="Payroll Processing Fee" value={data.payroll_processing_fee} onChange={(v) => set('payroll_processing_fee', v)} />
      </SimpleGrid>

      <HStack mt={2}>
        <Heading size="xs">State Tax Configuration</Heading>
        <IconButton
          aria-label="Add state"
          icon={<FaPlus />}
          size="xs"
          onClick={addState}
        />
      </HStack>

      {Object.entries(stateConfig).map(([state, taxes]) => (
        <Box key={state} p={2} borderWidth={1} borderRadius="md" borderColor="gray.100">
          <HStack mb={1}>
            <Heading size="xs">{state}</Heading>
            <IconButton
              aria-label={`Remove ${state}`}
              icon={<FaTrash />}
              size="xs"
              variant="ghost"
              colorScheme="red"
              onClick={() => removeState(state)}
            />
          </HStack>
          <SimpleGrid columns={3} spacing={2}>
            <NumField label="FICA" value={taxes.fica_tax} onChange={(v) => updateStateTax(state, 'fica_tax', v)} />
            <NumField label="FUTA" value={taxes.futa_tax} onChange={(v) => updateStateTax(state, 'futa_tax', v)} />
            <NumField label="SUTA" value={taxes.suta_tax} onChange={(v) => updateStateTax(state, 'suta_tax', v)} />
          </SimpleGrid>
        </Box>
      ))}

      <Button
        colorScheme="purple"
        size="sm"
        onClick={onSubmit}
        isLoading={isSubmitting}
        alignSelf="flex-start"
      >
        Apply Quote Config
      </Button>
    </VStack>
  );
}

function NumField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: any;
  onChange: (v: number) => void;
}) {
  return (
    <FormControl>
      <FormLabel fontSize="xs">{label}</FormLabel>
      <Input
        size="sm"
        type="number"
        step="any"
        value={value ?? ''}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </FormControl>
  );
}
